/**
 * Figma Designs Section Component
 * Displays Figma frames linked to a PKB entry
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Link,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Add as AddIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';
import { SV } from '../../automation/stratovaiTestIds';

/** Matches API `figma_sync_constants.FIGMA_SYNC_MANUAL_INTERVENTION` for client-side branching. */
const MANUAL_INTERVENTION_STATUS = 'manual_intervention_required';
const TOKEN_MISSING_STATUS = 'token_missing';
const TOKEN_EXPIRED_STATUS = 'token_expired';

const MANUAL_INTERVENTION_HELP =
  'Figma has returned a long rate-limit delay. Visual sync is paused until the scheduled retry time. Check the connected Figma plan/token or try again later.';

function formatSyncStatusMessage(visualSync, framesCount = 0) {
  if (!visualSync) {
    return framesCount > 0
      ? 'Figma frames available'
      : 'No frames found yet. Figma is linked — use Sync designs to fetch frames.';
  }
  const st = visualSync.aggregate_status;
  if (visualSync.user_message) {
    return visualSync.user_message;
  }
  const byStatus = {
    pending: 'Figma linked — visual sync queued',
    processing: 'Figma linked — visual sync running',
    failed: 'Figma sync failed — use Sync designs to retry',
    rate_limited: 'Figma rate limit cooldown — retry after the scheduled time',
    manual_intervention_required: MANUAL_INTERVENTION_HELP,
    token_missing:
      'Figma API token is not configured. Update Settings → Tokens & Security, then sync designs.',
    token_expired:
      'Figma API token expired or was rejected. Update the token in Settings → Tokens & Security, then retry.',
    completed: framesCount > 0 ? 'Figma frames available' : 'Figma linked — no frames found yet',
    partial: 'Figma visual sync partially completed — Sync designs to finish'
  };
  return byStatus[st] || 'Figma linked — use Sync designs to fetch frames';
}

function syncActionLabel(visualSync, hasFrames) {
  if (hasFrames) {
    return null;
  }
  const st = visualSync?.aggregate_status;
  if (st === TOKEN_MISSING_STATUS || st === TOKEN_EXPIRED_STATUS || st === 'failed') {
    return 'Retry visual sync';
  }
  return 'Sync designs';
}

const FigmaDesignsSection = ({ pkbEntryId, projectId, initialFrames = [], hasFigma = false, onFramesUpdated }) => {
  const [frames, setFrames] = useState(initialFrames);
  const [visualSync, setVisualSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [error, setError] = useState(null);
  const [refreshWarning, setRefreshWarning] = useState(null);
  const [success, setSuccess] = useState(null);

  const onFramesUpdatedRef = useRef(onFramesUpdated);
  useEffect(() => {
    onFramesUpdatedRef.current = onFramesUpdated;
  }, [onFramesUpdated]);

  useEffect(() => {
    setFrames(Array.isArray(initialFrames) ? initialFrames : []);
  }, [pkbEntryId, initialFrames]);

  const loadFrames = useCallback(async ({ explicit = false, refreshThumbnails = false } = {}) => {
    if (!pkbEntryId || !projectId) return;

    setLoading(true);
    if (explicit) {
      setError(null);
      setRefreshWarning(null);
    }

    const params = new URLSearchParams({ project_id: String(projectId) });
    if (refreshThumbnails) {
      params.set('refresh_thumbnails', 'true');
    }
    const framesPath = `/api/pkb/${encodeURIComponent(pkbEntryId)}/figma/frames?${params.toString()}`;

    try {
      const response = await API_CONFIG.request(framesPath, { method: 'GET' });
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const detail =
          data?.detail || data?.message || `Failed to load Figma frames (${response.status})`;
        if (explicit) {
          setError(detail);
        } else {
          setRefreshWarning(detail);
        }
        return;
      }

      if (data?.visual_sync) {
        setVisualSync(data.visual_sync);
      }

      const apiFrames = Array.isArray(data?.frames) ? data.frames : [];
      if (apiFrames.length > 0) {
        setFrames(apiFrames);
        if (onFramesUpdatedRef.current) {
          onFramesUpdatedRef.current(apiFrames);
        }
        setRefreshWarning(null);
        return;
      }

      if (explicit) {
        setFrames([]);
        if (onFramesUpdatedRef.current) {
          onFramesUpdatedRef.current([]);
        }
        return;
      }

      setRefreshWarning(
        data?.visual_sync?.user_message ||
          data?.message ||
          'Refresh returned no frames; showing frames loaded from the document.'
      );
    } catch (e) {
      console.error('Failed to load Figma frames:', e);
      const message = e.message || 'Failed to load Figma frames';
      if (explicit) {
        setError(message);
      } else {
        setRefreshWarning(message);
      }
    } finally {
      setLoading(false);
    }
  }, [pkbEntryId, projectId]);

  const handleVisualSyncRetry = useCallback(async () => {
    if (!pkbEntryId || !projectId) return;

    setSyncing(true);
    setError(null);
    setSuccess(null);
    setRefreshWarning(null);

    const params = new URLSearchParams({ project_id: String(projectId) });
    const syncPath = `/api/pkb/${encodeURIComponent(pkbEntryId)}/figma/sync?${params.toString()}`;

    try {
      const response = await API_CONFIG.request(syncPath, { method: 'POST' });
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        const detail =
          data?.detail || data?.message || `Visual sync request failed (${response.status})`;
        setError(detail);
        return;
      }

      const status = data?.status;
      const msg = data?.message || 'Visual sync updated';
      setVisualSync((prev) => ({
        ...(prev || {}),
        aggregate_status: status,
        user_message: msg,
        active_background_job_id: data?.job_id || prev?.active_background_job_id,
        next_retry_at: data?.next_retry_at ?? prev?.next_retry_at
      }));

      if (status === TOKEN_MISSING_STATUS || status === TOKEN_EXPIRED_STATUS) {
        setError(msg);
        return;
      }

      if (status === 'cooldown') {
        const retryHint = data?.next_retry_at ? ` Retry after ${data.next_retry_at}.` : '';
        setRefreshWarning(`${msg}${retryHint}`);
        return;
      }

      if (status === 'failed') {
        setError(msg);
        return;
      }

      if (status === 'completed') {
        setSuccess(msg);
        await loadFrames({ explicit: true });
        return;
      }

      const jobNote = data?.job_id ? ` Job ID: ${data.job_id}.` : '';
      setSuccess(`${msg}${jobNote}`);
      await loadFrames({ explicit: false });
    } catch (e) {
      console.error('Failed to queue Figma visual sync:', e);
      setError(e.message || 'Failed to queue Figma visual sync');
    } finally {
      setSyncing(false);
    }
  }, [pkbEntryId, projectId, loadFrames]);

  const didAutoLoadRef = useRef(false);
  useEffect(() => {
    if (!pkbEntryId || !projectId || !hasFigma) return;

    const initial = Array.isArray(initialFrames) ? initialFrames : [];
    const missingThumbnails = initial.length === 0 || initial.some((f) => !f?.thumbnail_url);
    const missingMetadata =
      initial.length > 0 && initial.some((f) => f && !f.page_name && !f.module_name);

    if (missingThumbnails || missingMetadata) {
      if (didAutoLoadRef.current !== pkbEntryId) {
        didAutoLoadRef.current = pkbEntryId;
        loadFrames({ explicit: false });
      }
    }
  }, [pkbEntryId, projectId, hasFigma, initialFrames, loadFrames]);

  const manualInterventionActive =
    visualSync?.aggregate_status === MANUAL_INTERVENTION_STATUS ||
    visualSync?.has_manual_intervention;

  const manualInterventionMessage =
    (manualInterventionActive && (visualSync?.user_message || MANUAL_INTERVENTION_HELP)) || null;

  const hasFrames = frames.length > 0;
  const primarySyncLabel = syncActionLabel(visualSync, hasFrames);
  const statusMessage = formatSyncStatusMessage(visualSync, frames.length);

  const handleLinkFigma = async () => {
    if (!figmaUrl.trim()) {
      setError('Please enter a Figma URL');
      return;
    }

    setLinking(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await API_CONFIG.request(`/api/pkb/${pkbEntryId}/figma/link`, {
        method: 'POST',
        body: JSON.stringify({ figma_url: figmaUrl.trim() })
      });
      const data = await response.json();

      if (data.success) {
        if (data.deferred_to_background) {
          setSuccess(data.message || 'Figma linked — visual sync queued in the background.');
        } else {
          setSuccess(`Successfully linked Figma file. Ingested ${data.frames_ingested} frames.`);
        }
        setFigmaUrl('');
        await loadFrames({ explicit: false });
      } else {
        setError(data.message || 'Failed to link Figma URL');
      }
    } catch (e) {
      console.error('Failed to link Figma:', e);
      setError(e.message || 'Failed to link Figma URL. Please check the URL and try again.');
    } finally {
      setLinking(false);
    }
  };

  const getFigmaFrameUrl = (frame) => {
    if (!frame.figma_url || !frame.figma_file_key || !frame.frame_id) {
      return null;
    }
    const baseUrl = frame.figma_url.split('/file/')[0] || 'https://www.figma.com';
    return `${baseUrl}/file/${frame.figma_file_key}?node-id=${frame.frame_id.replace(':', '%3A')}`;
  };

  if (!hasFigma && frames.length === 0) {
    return (
      <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Designs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Attach Figma design files to this PRD to provide visual context.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label="Figma File or Frame URL"
            placeholder="https://www.figma.com/file/..."
            value={figmaUrl}
            onChange={(e) => setFigmaUrl(e.target.value)}
            helperText="Paste a Figma file URL or frame URL to link designs"
            disabled={linking}
          />
          <Button
            variant="contained"
            startIcon={linking ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleLinkFigma}
            disabled={!figmaUrl.trim() || linking}
            sx={{ minWidth: 120 }}
          >
            {linking ? 'Linking...' : 'Link Figma'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Designs
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {primarySyncLabel && (
            <Button
              size="small"
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              onClick={handleVisualSyncRetry}
              disabled={syncing || loading}
              title="Queue or retry Figma visual sync for this entry"
            >
              {syncing ? 'Syncing...' : primarySyncLabel}
            </Button>
          )}
          {hasFrames && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadFrames({ explicit: true, refreshThumbnails: true })}
              disabled={loading || syncing}
              title="Refresh thumbnails from Figma (explicit; may use Figma API)"
            >
              {loading ? 'Refreshing...' : 'Refresh thumbnails'}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setFigmaUrl('');
              setError(null);
              setSuccess(null);
            }}
          >
            Add Another
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {refreshWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRefreshWarning(null)}>
          {refreshWarning}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {manualInterventionActive && manualInterventionMessage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {manualInterventionMessage}
          {typeof visualSync?.manual_intervention_link_count === 'number' &&
            visualSync.manual_intervention_link_count > 1 && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {visualSync.manual_intervention_link_count} linked Figma URLs need attention
                for this entry.
              </Typography>
            )}
        </Alert>
      )}

      {figmaUrl !== null && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              size="small"
              label="Add Another Figma URL"
              placeholder="https://www.figma.com/file/..."
              value={figmaUrl}
              onChange={(e) => setFigmaUrl(e.target.value)}
              disabled={linking}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={linking ? <CircularProgress size={14} /> : <AddIcon />}
              onClick={handleLinkFigma}
              disabled={!figmaUrl.trim() || linking}
            >
              {linking ? 'Linking...' : 'Link'}
            </Button>
            <IconButton
              size="small"
              onClick={() => setFigmaUrl(null)}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : frames.length === 0 ? (
        <Alert
          severity={
            visualSync?.aggregate_status === TOKEN_MISSING_STATUS ||
            visualSync?.aggregate_status === TOKEN_EXPIRED_STATUS
              ? 'error'
              : manualInterventionActive
                ? 'warning'
                : 'info'
          }
        >
          {statusMessage}
          {visualSync?.active_background_job_id && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Background job: {visualSync.active_background_job_id}
              {visualSync.active_background_job_status
                ? ` (${visualSync.active_background_job_status})`
                : ''}
            </Typography>
          )}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {frames.map((frame) => {
            const frameUrl = getFigmaFrameUrl(frame);
            return (
              <Grid item xs={12} sm={6} md={4} key={frame.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {frame.thumbnail_url ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={frame.thumbnail_url}
                      alt={frame.frame_name || 'Figma frame'}
                      data-testid={SV.pk.figmaFrameImage}
                      loading="eager"
                      sx={{ objectFit: 'contain', bgcolor: 'grey.100' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ px: 1, textAlign: 'center' }}>
                        {manualInterventionMessage && !frame.thumbnail_url
                          ? manualInterventionMessage
                          : visualSync?.user_message && !frame.thumbnail_url
                            ? visualSync.user_message
                            : 'No thumbnail'}
                      </Typography>
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {frame.frame_name || 'Unnamed Frame'}
                        </Typography>
                        {(frame.page_name || frame.module_name) && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {frame.page_name && (
                              <Chip
                                label={`Page: ${frame.page_name}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: '18px' }}
                              />
                            )}
                            {frame.module_name && (
                              <Chip
                                label={`Module: ${frame.module_name}`}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: '0.65rem', height: '18px' }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      {frameUrl && (
                        <IconButton
                          size="small"
                          component={Link}
                          href={frameUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ ml: 1 }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {frame.design_text && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mt: 1
                        }}
                      >
                        {frame.design_text}
                      </Typography>
                    )}
                    {frame.figma_file_name && (
                      <Chip
                        label={frame.figma_file_name}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default FigmaDesignsSection;
